import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Calendar,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Eye,
  Loader2,
  RefreshCw,
  Search,
  UserPlus,
} from "lucide-react";

import Badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";

import { ApplicantsKeys, getApplicants } from "../api/applicants";
import {
  getApplicantStatusColor,
  getApplicantStatusLabel,
  getMeetingVariantColor,
  getMeetingVariantLabel,
} from "../types/applicants";
import { getCountryName } from "../utils/countries";
import ApplicantDetailsDialog from "./ApplicantDetailsDialog";
import ChangeCohortDialog from "./ChangeCohortDialog";
import ConvertToFounderDialog from "./ConvertToFounderDialog";

const ApplicantsTable: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [viewApplicantId, setViewApplicantId] = useState<string | null>(null);
  const [changeCohortApplicant, setChangeCohortApplicant] = useState<{
    id: string;
    name: string;
    currentCohortId?: string | null;
  } | null>(null);
  const [convertFounderApplicant, setConvertFounderApplicant] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);

  const { toast } = useToast();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const skip = (currentPage - 1) * itemsPerPage;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      ApplicantsKeys.GET_APPLICANTS,
      debouncedSearchTerm,
      sortBy,
      sortOrder,
      skip,
      itemsPerPage,
    ],
    queryFn: () =>
      getApplicants(debouncedSearchTerm, sortBy, sortOrder, skip, itemsPerPage),
  });

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) {
      return <ChevronsUpDown className="h-4 w-4 ml-1" />;
    }
    return sortOrder === "asc" ? (
      <ChevronUp className="h-4 w-4 ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 ml-1" />
    );
  };

  const applicants = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / itemsPerPage);

  const handleRefresh = async () => {
    await refetch();
    toast({
      title: "Refreshed",
      description: "Applicant list has been refreshed",
    });
  };

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-destructive">
          Failed to load applicants. Please try again.
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-6">
        <div className="space-y-4">
          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, country, or status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 items-center">
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => {
                  setItemsPerPage(parseInt(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 per page</SelectItem>
                  <SelectItem value="25">25 per page</SelectItem>
                  <SelectItem value="50">50 per page</SelectItem>
                  <SelectItem value="100">100 per page</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefresh}
                disabled={isLoading}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                />
              </Button>
            </div>
          </div>

          {/* Results Summary */}
          <div className="text-sm text-muted-foreground">
            {isLoading ? (
              <span>Loading...</span>
            ) : (
              <span>
                Showing {applicants.length > 0 ? skip + 1 : 0} to{" "}
                {Math.min(skip + itemsPerPage, total)} of {total} applicant
                {total !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("firstName")}
                  >
                    <div className="flex items-center">
                      Name
                      {getSortIcon("firstName")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("email")}
                  >
                    <div className="flex items-center">
                      Email
                      {getSortIcon("email")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("country")}
                  >
                    <div className="flex items-center">
                      Country
                      {getSortIcon("country")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center">
                      Status
                      {getSortIcon("status")}
                    </div>
                  </TableHead>
                  <TableHead>Meeting Type</TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("createdAt")}
                  >
                    <div className="flex items-center">
                      Created
                      {getSortIcon("createdAt")}
                    </div>
                  </TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        Loading applicants...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : applicants.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {debouncedSearchTerm
                        ? `No applicants found matching "${debouncedSearchTerm}"`
                        : "No applicants found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  applicants.map((applicant) => (
                    <TableRow
                      key={applicant._id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setViewApplicantId(applicant._id)}
                    >
                      <TableCell>
                        {`${applicant.firstName} ${applicant.lastName}`}
                      </TableCell>
                      <TableCell>{applicant.email}</TableCell>
                      <TableCell>{getCountryName(applicant.country)}</TableCell>
                      <TableCell>
                        <Badge
                          color={getApplicantStatusColor(applicant.status)}
                          variant="full"
                        >
                          {getApplicantStatusLabel(applicant.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          color={getMeetingVariantColor(
                            applicant.meetingVariant,
                          )}
                          variant="outline"
                        >
                          {getMeetingVariantLabel(applicant.meetingVariant)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {applicant.createdAt
                          ? format(new Date(applicant.createdAt), "dd/MM/yyyy")
                          : "N/A"}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewApplicantId(applicant._id)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setChangeCohortApplicant({
                                id: applicant._id,
                                name: `${applicant.firstName} ${applicant.lastName}`,
                                currentCohortId:
                                  typeof applicant.cohort === "object"
                                    ? applicant.cohort?._id
                                    : applicant.cohort,
                              })
                            }
                            title="Change Cohort"
                          >
                            <Calendar className="h-4 w-4" />
                          </Button>
                          {!applicant.licenseeId && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setConvertFounderApplicant({
                                  id: applicant._id,
                                  name: `${applicant.firstName} ${applicant.lastName}`,
                                  email: applicant.email,
                                })
                              }
                              title="Convert to Founder"
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className={
                      currentPage === 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>

                {/* Show first page */}
                {currentPage > 2 && (
                  <>
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => setCurrentPage(1)}
                        className="cursor-pointer"
                      >
                        1
                      </PaginationLink>
                    </PaginationItem>
                    {currentPage > 3 && (
                      <PaginationItem>
                        <span className="px-4">...</span>
                      </PaginationItem>
                    )}
                  </>
                )}

                {/* Show current and adjacent pages */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (page) =>
                      page === currentPage ||
                      page === currentPage - 1 ||
                      page === currentPage + 1,
                  )
                  .map((page) => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => setCurrentPage(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                {/* Show last page */}
                {currentPage < totalPages - 1 && (
                  <>
                    {currentPage < totalPages - 2 && (
                      <PaginationItem>
                        <span className="px-4">...</span>
                      </PaginationItem>
                    )}
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => setCurrentPage(totalPages)}
                        className="cursor-pointer"
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  </>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    className={
                      currentPage === totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      </Card>

      {/* Details Dialog */}
      <ApplicantDetailsDialog
        applicantId={viewApplicantId}
        onClose={() => setViewApplicantId(null)}
      />

      {/* Change Cohort Dialog */}
      <ChangeCohortDialog
        applicant={changeCohortApplicant}
        onClose={() => setChangeCohortApplicant(null)}
      />

      {/* Convert to Founder Dialog */}
      <ConvertToFounderDialog
        applicant={convertFounderApplicant}
        onClose={() => setConvertFounderApplicant(null)}
      />
    </>
  );
};

export default ApplicantsTable;
